import React, { ReactNode, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { hashQueryKey, useQueries } from '@tanstack/react-query';
import * as ACL from '@/domain/restriction';
import { showApiErrorToast } from '@/services/api';
import type { RouteWaitQueries, RouteWaitQuery } from '@/domain/router/waitRoutesQueries';
import { useIsCompanyScopeBehind } from '@/hooks/useIsCompanyScopeBehind';

import Route404 from '@/pages/servicePage/page404';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';

interface RestrictedRouteProps {
  children: ReactNode;
  restriction: any;
  waitForQueries?: RouteWaitQueries;
}

// One empty list for every route that waits for no query, so its reference never changes.
const noQueries: RouteWaitQuery[] = [];

// The equality for useSelector: a route's declaration builds a new list on every call, so comparing references
// would re-render the guard on every Redux action. Two lists are the same when they name the same queries — the
// keys compared by TanStack's own hash, which ignores the order of object fields.
const haveSameQueryKeys = (a: RouteWaitQuery[], b: RouteWaitQuery[]) =>
  hashQueryKey(a.map((query) => query.queryKey)) === hashQueryKey(b.map((query) => query.queryKey));

const RestrictedRoute: React.FC<RestrictedRouteProps> = ({ children, restriction, waitForQueries }) => {
  const restrictions = useSelector(ACL.myRestriction);
  const ACLFilterFN = ACL.arrayFilter(restrictions);
  const isAvailable = ACLFilterFN({ restriction });

  const isScopeBehind = useIsCompanyScopeBehind();

  const selectWaitQueries = (state: unknown) => (waitForQueries ? waitForQueries(state) : noQueries);
  const waitQueries = useSelector(selectWaitQueries, haveSameQueryKeys);
  const waitResults = useQueries({ queries: waitQueries });
  const areQueriesReady = waitResults.every((query) => query.isSuccess);
  const waitError = waitResults.find((query) => query.isError)?.error;

  // A route that cannot open must not wait silently: the user sees why.
  useEffect(() => {
    if (waitError) showApiErrorToast(waitError);
  }, [waitError]);

  if (isScopeBehind || !areQueriesReady) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!isAvailable) {
    return <Route404 />;
  }

  return <>{children}</>;
};

export default RestrictedRoute;
